import React, { useState, useEffect } from 'react';
import { Lock, User, LogIn, Zap, Code2, Brain, ArrowRight } from 'lucide-react';
import { authAPI } from '../Intercepter/APiClient';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  
  useEffect(() => {
    setAnimateIn(true);
    
    if (authAPI.isAuthenticated()) {
      console.log('User already authenticated');
      window.location.href = '/console';
    }
  }, []);
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    setErrorMessage('');
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await authAPI.login(email, password);
      
      if (result.success) {
        console.log('Login successful');
        
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('userEmail', email);
        }
        
        window.location.href = '/console';
      } else {
        setErrorMessage(result.message || 'Login failed');
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && email && password && !isLoading) {
      handleSubmit(e);
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-8 relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-2xl">
        {/* Version Badge */}
        <div 
          className={`mb-8 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-md border border-cyan-500/40 rounded-full px-4 py-2 transition-all duration-1000 ${animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        >
          <Zap size={16} className="text-cyan-400 animate-pulse" />
          <span className="text-cyan-300 text-sm font-bold">⚡ UPDATED: v6.2.0 "GOD MODE" RELEASED</span>
        </div>

        {/* Main Heading */}
        <div 
          className={`text-center mb-12 transition-all duration-1000 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h1 className="text-5xl md:text-6xl font-black mb-4 leading-tight">
            <span className="text-white">The Smartest Way to</span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent">
              Master Assessments.
            </span>
          </h1>
        </div>

        {/* Login Form Card */}
        <div 
          className={`w-full bg-gray-950/50 backdrop-blur-lg border border-gray-800 rounded-2xl p-8 md:p-10 transition-all duration-1000 ${animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        >
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Admin Console</h2>
            <p className="text-gray-400">Access your assessment dashboard</p>
          </div>
          
          <div className="space-y-6">
            {errorMessage && (
              <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm animate-pulse">
                {errorMessage}
              </div>
            )}
            
            {/* Email Input */}
            <div className="relative group">
              <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors ${focusedField === 'email' ? 'text-cyan-400' : 'text-gray-500'}`}>
                <User size={20} />
              </div>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                onKeyPress={handleKeyPress}
                className="w-full py-3 pl-12 pr-4 bg-gray-900 text-white border border-gray-800 rounded-lg outline-none focus:border-cyan-500 focus:bg-gray-900/80 transition-all duration-300 placeholder:text-gray-600"
                required
              />
            </div>
            
            {/* Password Input */}
            <div className="relative group">
              <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors ${focusedField === 'password' ? 'text-cyan-400' : 'text-gray-500'}`}>
                <Lock size={20} />
              </div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                onKeyPress={handleKeyPress}
                className="w-full py-3 pl-12 pr-4 bg-gray-900 text-white border border-gray-800 rounded-lg outline-none focus:border-cyan-500 focus:bg-gray-900/80 transition-all duration-300 placeholder:text-gray-600"
                required
              />
            </div>
            
            {/* Remember & Forgot */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  className="h-4 w-4 rounded border-gray-700 bg-gray-900 accent-cyan-500 cursor-pointer"
                />
                <label htmlFor="remember-me" className="text-gray-400 hover:text-gray-300 cursor-pointer transition">
                  Remember me
                </label>
              </div>
              <a href="/forgot-password" className="text-cyan-400 hover:text-cyan-300 transition font-medium">
                Forgot password?
              </a>
            </div>
            
            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isLoading || !email || !password}
              className={`w-full relative group flex items-center justify-center py-3 px-4 font-bold rounded-lg text-white transition-all duration-300 overflow-hidden mt-8 ${
                isLoading || !email || !password
                  ? 'bg-gradient-to-r from-gray-600 to-gray-700 opacity-50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 hover:shadow-lg hover:shadow-cyan-500/50'
              }`}
            >
              <div className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign in to Dashboard</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </div>
            </button>
          </div>
          
          {/* Footer */}
          <div className="mt-8 text-center text-sm">
            <p className="text-gray-400">
              Don't have an account? <span className="text-cyan-400 hover:text-cyan-300 cursor-pointer font-semibold transition">Contact administrator</span>
            </p>
          </div>
        </div>

        {/* Features Grid Below */}
        <div className="mt-12 w-full grid grid-cols-3 gap-4 md:gap-6">
          {[
            { icon: Brain, title: 'Neural Driver Host', desc: 'GPU-powered inference' },
            { icon: Code2, title: 'Code Intelligence', desc: 'Debug & analyze instantly' },
            { icon: Zap, title: 'Ultra-Fast', desc: '30-50 tokens/sec' }
          ].map((feature, idx) => (
            <div 
              key={idx}
              className={`text-center transition-all duration-1000 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{transitionDelay: `${200 + idx * 100}ms`}}
            >
              <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 p-4 rounded-lg mb-3 inline-block">
                <feature.icon size={24} className="text-cyan-400" />
              </div>
              <h3 className="text-white font-bold text-sm">{feature.title}</h3>
              <p className="text-gray-500 text-xs mt-1">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}